import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import {
  ClipboardCheck,
  Wrench,
  ShieldCheck,
  CalendarCheck,
} from "lucide-react";

const processSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "@id": "https://www.saskroofingcompany.com/process#howto",
  name: "Sask Roofing Company 4-Step Roof Replacement Process",
  description:
    "Our transparent 4-step roofing process from free inspection through final walkthrough.",
  totalTime: "P3D",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Free Inspection",
      text: "We start with a comprehensive roof inspection at no cost and no obligation. We examine shingles, flashings, eavestroughs, and attic ventilation. You get a same-day diagnosis.",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Custom Quote & Selection",
      text: "You receive a detailed, fixed-price written quote and help selecting the right materials for your home and Saskatchewan climate.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Professional Installation",
      text: "Our local crew installs your new roof per the quote. Most residential roof replacements are completed in 1-2 days, weather permitting. We work year-round including Saskatchewan winters.",
    },
    {
      "@type": "HowToStep",
      position: 4,
      name: "Final Walkthrough & Warranty",
      text: "We do a final walkthrough and review everything together. Your warranty is in writing — for both workmanship and manufacturer materials.",
    },
  ],
};

export default function Process() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Our Roofing Process | Sask Roofing Company"
        description="Learn about our simple, transparent 3-step roofing process for Saskatchewan homeowners. Free inspections, honest quotes, and professional installation."
        schemaJson={[processSchema]}
      />

      {/* Hero Section */}
      <section className="relative w-full py-24 md:py-32 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/40828d16-935f-4087-8b3c-2dd81ee3ce2a.png&output=webp&q=60"
            alt="Roofing crew starting a project on a Saskatchewan home"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>

        <div className="container relative z-10 px-4 md:px-6">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Our <span className="text-accent">Process</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl">
              Getting a new roof shouldn't be stressful. We've built a
              straightforward, transparent process to protect your home with
              zero surprises.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-16">
            {/* Step 1 */}
            <div className="flex flex-col md:flex-row gap-8 items-start relative">
              <div className="hidden md:block absolute left-[31px] top-20 bottom-[-4rem] w-0.5 bg-border -z-10"></div>
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold border-4 border-background shrink-0 z-10 shadow-sm">
                1
              </div>
              <div className="space-y-4 pt-2 flex-grow">
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="w-8 h-8 text-primary" />
                  <h2 className="text-3xl font-bold text-foreground">
                    The Free Inspection
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 items-start">
                  <div className="space-y-4">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      It starts with a thorough, no-obligation assessment of
                      your roof. We don't just look at the shingles; we check
                      the decking, ventilation, flashing, and eavestroughs. We
                      document everything with photos so you can see exactly
                      what we see.
                    </p>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Full exterior perimeter check
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Attic ventilation and insulation review
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Storm and hail damage assessment
                      </li>
                    </ul>
                  </div>
                  <img
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a12e523b-5266-4a44-b309-2d8f4e5a888e.png&output=webp&q=60"
                    alt="Roof inspection"
                    className="rounded-xl object-cover w-full h-48 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col md:flex-row gap-8 items-start relative">
              <div className="hidden md:block absolute left-[31px] top-20 bottom-[-4rem] w-0.5 bg-border -z-10"></div>
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold border-4 border-background shrink-0 z-10 shadow-sm">
                2
              </div>
              <div className="space-y-4 pt-2 flex-grow">
                <div className="flex items-center gap-3">
                  <CalendarCheck className="w-8 h-8 text-primary" />
                  <h2 className="text-3xl font-bold text-foreground">
                    Custom Quote & Selection
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 items-start">
                  <div className="space-y-4">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      We sit down with you to review the findings. You'll
                      receive a detailed, fixed-price quote in writing. No
                      hidden fees, no high-pressure sales tactics. We'll help
                      you select the right materials designed to withstand
                      Saskatchewan's climate.
                    </p>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Honest, transparent pricing
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Material selection (Asphalt, Metal, etc.)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Project timeline scheduling
                      </li>
                    </ul>
                  </div>
                  <img
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2622416b-4835-46e5-b1a3-2c136f455988.png&output=webp&q=60"
                    alt="Providing a quote"
                    className="rounded-xl object-cover w-full h-48 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col md:flex-row gap-8 items-start relative">
              <div className="hidden md:block absolute left-[31px] top-20 bottom-[-4rem] w-0.5 bg-border -z-10"></div>
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold border-4 border-background shrink-0 z-10 shadow-sm">
                3
              </div>
              <div className="space-y-4 pt-2 flex-grow">
                <div className="flex items-center gap-3">
                  <Wrench className="w-8 h-8 text-primary" />
                  <h2 className="text-3xl font-bold text-foreground">
                    Professional Installation
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 items-start">
                  <div className="space-y-4">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Our local, experienced crews arrive on time and get to
                      work. We protect your landscaping, tear off the old roof,
                      and install your new system according to strict
                      manufacturer specifications. Most residential roofs are
                      completed in 1-2 days.
                    </p>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Property protection (tarps and covers)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Efficient, safe tear-off and disposal
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Precision installation by certified roofers
                      </li>
                    </ul>
                  </div>
                  <img
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/40828d16-935f-4087-8b3c-2dd81ee3ce2a.png&output=webp&q=60"
                    alt="Roof installation"
                    className="rounded-xl object-cover w-full h-48 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex flex-col md:flex-row gap-8 items-start relative">
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold border-4 border-background shrink-0 z-10 shadow-sm">
                4
              </div>
              <div className="space-y-4 pt-2 flex-grow">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-8 h-8 text-primary" />
                  <h2 className="text-3xl font-bold text-foreground">
                    Final Walkthrough & Warranty
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 items-start">
                  <div className="space-y-4">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      We don't leave until the job is perfect. We perform a
                      magnetic sweep of your yard to catch stray nails, clean up
                      all debris, and do a final walkthrough with you. Then, we
                      hand over your comprehensive warranty documentation.
                    </p>
                    <ul className="space-y-2 text-foreground">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Magnetic nail sweep and full cleanup
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Quality assurance inspection
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{" "}
                        Warranty registration
                      </li>
                    </ul>
                  </div>
                  <img
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/fe7242f1-b408-4417-8330-aaae1161669d.png&output=webp&q=60"
                    alt="Final completed roof"
                    className="rounded-xl object-cover w-full h-48 shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30 border-t">
        <div className="container px-4 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                What to Expect After You Book
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Once you sign your contract, we handle everything else. Here is
                how we prepare for a smooth, stress-free installation day:
              </p>
              <ul className="space-y-6 mt-6">
                <li className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold shrink-0 mt-1">
                    1
                  </span>
                  <div>
                    <h4 className="text-xl font-semibold mb-1">
                      Site Preparation
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      We coordinate material delivery and bin placement. We ask
                      that you clear your driveway and secure any fragile items
                      on your patio.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold shrink-0 mt-1">
                    2
                  </span>
                  <div>
                    <h4 className="text-xl font-semibold mb-1">Safety First</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Please keep pets indoors and children away from the work
                      zone. We also recommend notifying your neighbors about the
                      upcoming project.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold shrink-0 mt-1">
                    3
                  </span>
                  <div>
                    <h4 className="text-xl font-semibold mb-1">
                      Clear Communication
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Your dedicated project manager will keep you updated
                      throughout the build, answering any questions and ensuring
                      everything stays on track.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">Why Our Process Works</h2>
              <div className="bg-background p-8 rounded-xl border shadow-sm space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  We've refined our process over thousands of roofs across
                  Saskatchewan. By standardizing our approach, we eliminate the
                  common headaches associated with hiring a contractor.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Our local crews are highly skilled and trained on specific
                  manufacturer requirements. Because we provide honest,
                  no-day-of-surprises pricing, you never have to worry about
                  hidden fees. And because our warranties are real and backed by
                  our established local presence, you have true peace of mind
                  long after we leave your property.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-primary text-primary-foreground text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        <div className="container px-4 relative z-10 max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Ready to start the process?
          </h2>
          <p className="text-xl text-primary-foreground/80">
            Get a free, no-pressure inspection and a fixed-price quote from
            Saskatchewan's trusted roofing experts.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
            <Button
              size="lg"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-6 h-auto transition-colors"
              asChild
            >
              <Link to="/contact">Get Your Free Roof Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
